import {
	createOperationDescriptor,
	getRequest,
	getFragment,
	getSelector,
	type Disposable,
	type IEnvironment,
	type RecordSourceProxy,
} from 'relay-runtime';

import mainPaginationQuery from './__generated__/agentSessionsMainPaginationQuery.graphql';
import type { agentSessionsMainQuery$variables } from './__generated__/agentSessionsMainQuery.graphql';
import type {
	agentSessionsMainQuery_query$data,
	agentSessionsMainQuery_query$key,
} from './__generated__/agentSessionsMainQuery_query.graphql';
import { getAgentSessionEventSource, type AgentSessionEvent } from './agentSessionEventSource';
import { hydrateAgentSession, readAgentSessionDisplay } from './agentSessionHydration';
import {
	getAgentSessionReconciliation,
	hasAgentSessionDisplay,
	reconcileAgentSessionConnection,
} from './agentSessionReconciliation';
import { agentSessionsMainQuery, agentSessionsMainFragment } from './agentSessionsMainQuery';
import {
	AGENT_SESSIONS_PAGE_SIZE,
	AGENT_SESSIONS_SORT,
	getAgentSessionsPolicy,
} from './agentSessionsPolicy';
import {
	getAgentSessionsScopeKey,
	type ResolvedAgentSessionsScope,
} from './AgentSessionsScopeProvider';
import { createAgentSessionsQueryInputs } from './createAgentSessionsQueryInputs';
import { refreshSharedAgentSessions } from './refreshSharedAgentSessions';
import { SharedAgentSessionsResource } from './sharedAgentSessionsResource';

export type MainReference = Readonly<{
	fragment: agentSessionsMainQuery_query$key;
	pageInfo: { hasNextPage: boolean; endCursor: string | null };
	isLoadingMore: boolean;
	isRefreshing: boolean;
	loadMoreError: Error | null;
	refreshError: Error | null;
}>;
const asError = (error: unknown): Error =>
	error instanceof Error ? error : new Error('AgentSession request failed.');
// Only explicit cursor rejection gets a single rebase; other failures remain visible to the caller.
const isRejectedCursor = (error: Error): boolean =>
	/cursor/i.test(error.message) && /invalid|expired|stale|reject/i.test(error.message);

type Completion = (error: Error | null) => void;
type Driver = {
	hasNext: boolean;
	endCursor: string | null;
};

export class AgentSessionsMainResource extends SharedAgentSessionsResource<MainReference> {
	extent: number = AGENT_SESSIONS_PAGE_SIZE;
	initialFetchPolicy: 'store-or-network' | 'network-only' = 'network-only';
	private lastObservedRevision: number | undefined;
	private driver: Driver | undefined;
	private pendingDriver: (() => void) | undefined;
	private active:
		| {
				kind: 'more' | 'refresh';
				request?: Disposable;
				guard: Disposable;
				listeners: Set<Completion>;
				previousExtent: number;
				retained?: Disposable;
				retried: boolean;
		  }
		| undefined;
	private continuation: ReturnType<typeof setTimeout> | undefined;
	private queuedMore = new Set<Completion>();
	private queuedRefresh = new Set<Completion>();
	private retained: Disposable | undefined;
	private recoveryTimer: ReturnType<typeof setTimeout> | undefined;
	private pendingRecovery = false;
	private hydration = new Map<string, Disposable>();
	private attempted = new Map<string, number>();
	private runGeneration = 0;
	readonly inputs: ReturnType<typeof createAgentSessionsQueryInputs>;
	readonly includeLocal: boolean;
	constructor(
		readonly environment: IEnvironment,
		readonly scope: ResolvedAgentSessionsScope,
	) {
		super();
		this.includeLocal = getAgentSessionsPolicy().includeLocalAgentSessions;
		this.inputs = createAgentSessionsQueryInputs(
			scope.accountId,
			scope.rovoActivationId,
			this.includeLocal,
		);
	}
	get variables(): agentSessionsMainQuery$variables {
		return { ...this.inputs, count: this.extent, after: null };
	}
	private updateState(update: Partial<MainReference>): void {
		const { owner, generation, value } = this.getSnapshot();
		if (owner && value) this.publish(owner, generation, { ...value, ...update });
	}
	setDriver(driver: Driver): void {
		this.driver = driver;
		this.pendingDriver?.();
		if (this.pendingRecovery) this.scheduleRecovery();
	}
	private readPageInfo(): MainReference['pageInfo'] | undefined {
		const reference = this.getSnapshot().value?.fragment;
		const selector = reference && getSelector(getFragment(agentSessionsMainFragment), reference);
		if (!selector || selector.kind === 'PluralReaderSelector') return;
		const data = this.environment.lookup(selector).data as agentSessionsMainQuery_query$data;
		if (data.agent_sessionQuery?.edges == null) return;
		const pageInfo = data.agent_sessionQuery.pageInfo;
		if (pageInfo?.hasNextPage && pageInfo.endCursor == null) return;
		if (pageInfo) return { hasNextPage: pageInfo.hasNextPage, endCursor: pageInfo.endCursor };
	}
	private afterDriverCommit(pageInfo: MainReference['pageInfo'], done: () => void): void {
		// Network completion precedes the React commit that publishes the new cursor.
		// Keep the operation busy until both the driver and public snapshot describe that page.
		this.pendingDriver = () => {
			const published = this.getSnapshot().value?.pageInfo;
			if (
				this.driver?.endCursor !== pageInfo.endCursor ||
				this.driver.hasNext !== pageInfo.hasNextPage ||
				published?.endCursor !== pageInfo.endCursor ||
				published.hasNextPage !== pageInfo.hasNextPage
			)
				return;
			this.pendingDriver = undefined;
			done();
		};
		this.pendingDriver();
	}
	private reconcile = (store: RecordSourceProxy, event: AgentSessionEvent): void => {
		reconcileAgentSessionConnection(
			store,
			{
				key: 'agentSessionsMain_agent_sessionQuery',
				filters: {
					shardingContext: this.inputs.sharingContext,
					aqlQuery: this.inputs.aqlQuery,
					sort: AGENT_SESSIONS_SORT,
				},
				extent: this.extent,
				unseen: false,
				includeLocal: this.includeLocal,
			},
			event.sessionId,
			event.session === null,
			hasAgentSessionDisplay(store, event.sessionId),
			event.receivedAt,
		);
	};
	private event = (event: AgentSessionEvent): void => {
		const reconciliation = getAgentSessionReconciliation(this.environment, this.scope);
		reconciliation.event(event);
		this.environment.commitUpdate((store) => this.reconcile(store, event));
		if (!event.session) {
			this.hydration.get(event.sessionId)?.dispose();
			this.hydration.delete(event.sessionId);
			return;
		}
		if (
			event.session.lastStateChangeAt == null ||
			(!this.includeLocal && event.session.invocationType == null)
		) {
			this.scheduleRecovery();
			return;
		}
		if (!this.includeLocal && event.session.invocationType === 'LOCAL_AGENT_SESSION') return;
		const snapshot = readAgentSessionDisplay(this.environment, this.scope, event.sessionId);
		if (!snapshot.isMissingData) return;
		if (
			this.hydration.has(event.sessionId) ||
			this.attempted.get(event.sessionId) === event.sequence
		)
			return;
		this.attempted.set(event.sessionId, event.sequence);
		const generation = this.runGeneration;
		const guard = reconciliation.begin(event);
		let completed = false;
		const request = hydrateAgentSession(
			this.environment,
			this.scope,
			event.sessionId,
			({ found, error }) => {
				completed = true;
				this.hydration.delete(event.sessionId);
				guard.dispose();
				if (generation !== this.runGeneration || reconciliation.current(event.sessionId)?.removed)
					return;
				const current = readAgentSessionDisplay(this.environment, this.scope, event.sessionId);
				if (error || !found || current.isMissingData) this.scheduleRecovery();
				else this.environment.commitUpdate((store) => this.reconcile(store, event));
			},
		);
		if (!completed)
			this.hydration.set(event.sessionId, {
				dispose: () => {
					request.dispose();
					guard.dispose();
				},
			});
	};
	start(): Disposable {
		const generation = ++this.runGeneration;
		const reconciliation = getAgentSessionReconciliation(this.environment, this.scope);
		const source = getAgentSessionEventSource(this.environment, this.scope);
		this.initialFetchPolicy =
			this.getSnapshot().value && source.continuous && source.revision === this.lastObservedRevision
				? 'store-or-network'
				: 'network-only';
		const handler = reconciliation.addHandler(this.reconcile);
		const observer = source.observe((event) => {
			if (generation !== this.runGeneration) return;
			this.lastObservedRevision = event.sequence;
			if (event.kind === 'session') this.event(event);
			else this.scheduleRecovery();
		});
		this.retained ??= this.environment.retain(
			createOperationDescriptor(getRequest(agentSessionsMainQuery), this.variables),
		);
		const resume = () => {
			if (this.pendingRecovery) this.scheduleRecovery();
		};
		window.addEventListener('online', resume);
		document.addEventListener('visibilitychange', resume);
		return {
			dispose: () => {
				if (generation !== this.runGeneration) return;
				++this.runGeneration;
				this.lastObservedRevision = source.revision;
				observer.dispose();
				handler.dispose();
				this.driver = undefined;
				this.pendingDriver = undefined;
				clearTimeout(this.recoveryTimer);
				this.recoveryTimer = undefined;
				clearTimeout(this.continuation);
				this.continuation = undefined;
				this.active?.request?.dispose();
				this.active?.retained?.dispose();
				this.active?.guard.dispose();
				if (this.active) this.extent = this.active.previousExtent;
				this.active?.listeners.forEach((listener) =>
					listener(new Error('AgentSession owner changed.')),
				);
				this.active = undefined;
				const cancelled = new Error('AgentSession owner changed.');
				this.queuedMore.forEach((listener) => listener(cancelled));
				this.queuedRefresh.forEach((listener) => listener(cancelled));
				this.queuedMore.clear();
				this.queuedRefresh.clear();
				this.updateState({ isLoadingMore: false, isRefreshing: false });
				this.hydration.forEach((request) => request.dispose());
				this.hydration.clear();
				window.removeEventListener('online', resume);
				document.removeEventListener('visibilitychange', resume);
				if (!this.getSnapshot().owner) {
					this.retained?.dispose();
					this.retained = undefined;
					this.attempted.clear();
					this.clear();
					this.extent = AGENT_SESSIONS_PAGE_SIZE;
				}
			},
		};
	}
	private scheduleRecovery = (): void => {
		this.pendingRecovery = true;
		if (
			this.recoveryTimer ||
			this.active ||
			!this.getSnapshot().owner ||
			document.visibilityState !== 'visible' ||
			!navigator.onLine
		)
			return;
		this.recoveryTimer = setTimeout(() => {
			this.recoveryTimer = undefined;
			if (!this.driver) return;
			this.pendingRecovery = false;
			this.refresh();
		}, 1000);
	};
	private run(kind: 'more' | 'refresh', listener?: Completion): void {
		if (!this.driver || !this.getSnapshot().owner) {
			listener?.(new Error('AgentSessions owner is not ready.'));
			return;
		}
		if (this.active) {
			if (this.active.kind === kind) {
				if (listener) this.active.listeners.add(listener);
			} else (kind === 'more' ? this.queuedMore : this.queuedRefresh).add(listener ?? (() => {}));
			return;
		}
		if (kind === 'more' && !this.driver.hasNext) {
			listener?.(null);
			return;
		}
		const generation = this.runGeneration;
		const job = {
			kind,
			guard: getAgentSessionReconciliation(this.environment, this.scope).begin(),
			listeners: new Set(listener ? [listener] : []),
			request: undefined as Disposable | undefined,
			retained: undefined as Disposable | undefined,
			previousExtent: this.extent,
			retried: false,
		};
		// Reserve the requested capacity before Relay publishes the next page and replays events.
		if (kind === 'more') this.extent += AGENT_SESSIONS_PAGE_SIZE;
		this.active = job;
		this.updateState(
			kind === 'more'
				? { isLoadingMore: true, loadMoreError: null }
				: { isRefreshing: true, refreshError: null },
		);
		const finish = (error: Error | null) => {
			if (generation !== this.runGeneration || this.active !== job) return;
			if (error && kind === 'more') this.extent = job.previousExtent;
			if (job.retained) {
				if (error) job.retained.dispose();
				else {
					this.retained?.dispose();
					this.retained = job.retained;
				}
			}
			job.guard.dispose();
			this.active = undefined;
			job.request?.dispose();
			this.updateState(
				kind === 'more'
					? { isLoadingMore: false, loadMoreError: error }
					: { isRefreshing: false, refreshError: error },
			);
			job.listeners.forEach((callback) => callback(error));
			// Let the pagination owner receive the new cursor before draining the next intent.
			this.continuation = setTimeout(() => {
				this.continuation = undefined;
				if (generation !== this.runGeneration) return;
				if (this.queuedRefresh.size) {
					const callbacks = [...this.queuedRefresh];
					this.queuedRefresh.clear();
					this.refresh({
						onComplete: (failure) => callbacks.forEach((callback) => callback(failure)),
					});
				} else if (this.queuedMore.size) {
					const callbacks = [...this.queuedMore];
					this.queuedMore.clear();
					this.loadMore({
						onComplete: (failure) => callbacks.forEach((callback) => callback(failure)),
					});
				} else if (this.pendingRecovery) this.scheduleRecovery();
			}, 0);
		};
		const replace = (done: Completion): void => {
			// The old query stays retained until a complete replacement succeeds.
			const operation = createOperationDescriptor(getRequest(mainPaginationQuery), this.variables);
			job.retained?.dispose();
			job.retained = this.environment.retain(operation);
			let completed = false;
			const request = refreshSharedAgentSessions(
				this.environment,
				mainPaginationQuery,
				this.variables,
				(error) => {
					completed = true;
					done(error);
				},
			);
			job.request = request;
			if (completed) request.dispose();
		};
		const more = (): void => {
			const startingCursor = this.driver!.endCursor;
			let completed = false;
			const request = refreshSharedAgentSessions(
				this.environment,
				mainPaginationQuery,
				{ ...this.inputs, count: AGENT_SESSIONS_PAGE_SIZE, after: startingCursor },
				(error) => {
					completed = true;
					if (generation !== this.runGeneration || this.active !== job) return;
					if (error && !job.retried && isRejectedCursor(error)) {
						job.retried = true;
						this.extent = job.previousExtent;
						replace((failure) => {
							if (failure) {
								finish(failure);
								return;
							}
							const pageInfo = this.readPageInfo();
							if (!pageInfo) {
								finish(new Error('Missing AgentSession page information.'));
								return;
							}
							this.afterDriverCommit(pageInfo, () => {
								if (generation !== this.runGeneration || this.active !== job) return;
								if (!pageInfo.hasNextPage) {
									finish(null);
									return;
								}
								this.extent = job.previousExtent + AGENT_SESSIONS_PAGE_SIZE;
								try {
									more();
								} catch (cause) {
									finish(asError(cause));
								}
							});
						});
					} else if (error) finish(error);
					else {
						const pageInfo = this.readPageInfo();
						if (
							!pageInfo ||
							(pageInfo.hasNextPage &&
								(pageInfo.endCursor == null || pageInfo.endCursor === startingCursor))
						) {
							finish(new Error('AgentSession pagination did not advance its cursor.'));
							return;
						}
						this.afterDriverCommit(pageInfo, () => finish(null));
					}
				},
			);
			// A synchronous rejection can already have started a cursor-recovery request.
			if (completed) request.dispose();
			else job.request = request;
		};
		try {
			if (kind === 'more') more();
			else
				replace((error) => {
					const pageInfo = this.readPageInfo();
					if (error || !pageInfo)
						finish(error ?? new Error('Missing AgentSession page information.'));
					else this.afterDriverCommit(pageInfo, () => finish(null));
				});
		} catch (error) {
			finish(asError(error));
		}
	}
	readonly loadMore = (options?: { onComplete?: Completion }): void =>
		this.run('more', options?.onComplete);
	readonly refresh = (options?: { onComplete?: Completion }): Disposable => {
		let listening = true;
		this.run('refresh', (error) => {
			if (listening) options?.onComplete?.(error);
		});
		return {
			dispose: () => {
				listening = false;
			},
		};
	};
}

const resources = new WeakMap<IEnvironment, Map<string, AgentSessionsMainResource>>();
export const getAgentSessionsMainResource = (
	environment: IEnvironment,
	scope: ResolvedAgentSessionsScope,
): AgentSessionsMainResource => {
	let scopes = resources.get(environment);
	if (!scopes) {
		scopes = new Map();
		resources.set(environment, scopes);
	}
	const key = getAgentSessionsScopeKey(scope);
	let resource = scopes.get(key);
	if (!resource) {
		resource = new AgentSessionsMainResource(environment, scope);
		scopes.set(key, resource);
	}
	return resource;
};
